/**
 * TemplateVisibilityBadge - Visibility indicator for templates
 *
 * Displays template visibility with semantic color coding:
 * - all: emerald (public, visible to all tenants)
 * - plans: blue (restricted to specific plans)
 * - tenants: amber (restricted to specific tenants)
 * - none: slate (hidden/private)
 */
import { Globe, CreditCard, Building2, EyeOff } from 'lucide-react';
import { cn } from '@respectly/ui';
import type { TemplateVisibility } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface TemplateVisibilityBadgeProps {
  visibility: TemplateVisibility;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// ============================================
// Configuration
// ============================================

const visibilityConfig: Record<
  TemplateVisibility,
  { label: string; icon: typeof Globe; dotColor: string; badgeClass: string }
> = {
  all: {
    label: 'Public',
    icon: Globe,
    dotColor: 'bg-emerald-500',
    badgeClass:
      'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 border-emerald-500/20',
  },
  plans: {
    label: 'Plan-Specific',
    icon: CreditCard,
    dotColor: 'bg-blue-500',
    badgeClass:
      'bg-blue-500/10 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400 border-blue-500/20',
  },
  tenants: {
    label: 'Account-Specific',
    icon: Building2,
    dotColor: 'bg-amber-500',
    badgeClass:
      'bg-amber-500/10 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 border-amber-500/20',
  },
  none: {
    label: 'Hidden',
    icon: EyeOff,
    dotColor: 'bg-slate-500',
    badgeClass:
      'bg-slate-500/10 text-slate-600 dark:bg-slate-500/20 dark:text-slate-400 border-slate-500/20',
  },
};

const sizeClasses = {
  sm: 'px-1.5 py-0.5 text-[10px]',
  md: 'px-2 py-0.5 text-xs',
  lg: 'px-2.5 py-1 text-sm',
};

const iconSizeClasses = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

// ============================================
// Component
// ============================================

export function TemplateVisibilityBadge({
  visibility,
  showIcon = true,
  size = 'md',
  className,
}: TemplateVisibilityBadgeProps) {
  const config = visibilityConfig[visibility];
  const Icon = config.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        sizeClasses[size],
        config.badgeClass,
        className
      )}
    >
      {showIcon && <Icon className={iconSizeClasses[size]} />}
      {config.label}
    </span>
  );
}

// ============================================
// Utility Functions
// ============================================

export function getVisibilityColor(visibility: TemplateVisibility): string {
  const colors: Record<TemplateVisibility, string> = {
    all: '#10b981',
    plans: '#3b82f6',
    tenants: '#f59e0b',
    none: '#64748b',
  };
  return colors[visibility];
}

export function getVisibilityLabel(visibility: TemplateVisibility): string {
  return visibilityConfig[visibility].label;
}

export function getVisibilityIcon(visibility: TemplateVisibility) {
  return visibilityConfig[visibility].icon;
}
